import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };

const subjectColors = {
  english: { bg: '#007BFF', label: 'English' },
  math: { bg: '#28a745', label: 'Math' },
  science: { bg: '#6f42c1', label: 'Science' }
};

const API_BASE = '/api';

export default function ExamPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) fetchExamDetails();
  }, [id]);

  const fetchExamDetails = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/exams/${id}/questions`);
      if (!res.ok) throw new Error('Failed to fetch exam');
      const data = await res.json();
      setExam(data.exam);
      setQuestions(data.questions);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  // ✅ Find a question by its flow_id (not order_index!)
  const findQuestionByFlowId = (flowId) => {
    if (!flowId) return null;
    return questions.find(q => q.flow_id === flowId);
  };

  // ✅ Get display text for branching target
  const getBranchTargetText = (targetFlowId) => {
    if (!targetFlowId) return '🏁 End of exam';
    const target = findQuestionByFlowId(targetFlowId);
    if (!target) return '⚠️ Unknown target';
    const idx = questions.indexOf(target) + 1;
    return `Q${idx}: ${target.question_text.substring(0, 30)}...`;
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 60 }}>Loading exam...</div>;
  if (error) return <div style={{ padding: 20, color: '#721c24', background: '#f8d7da', borderRadius: 8, margin: '40px auto', maxWidth: 600 }}>❌ {error}<br/><button onClick={() => navigate('/admin/flow-builder-exams')} style={{ ...btnSecondary, marginTop: 10 }}>← Back</button></div>;
  if (!exam) return <div style={{ textAlign: 'center', marginTop: 60 }}>Exam not found.<br/><button onClick={() => navigate('/admin/view-exams')} style={{ ...btnSecondary, marginTop: 10 }}>← Back</button></div>;

  const colors = subjectColors[exam.exam_topic] || { bg: '#6c757d', label: exam.exam_topic };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Exam Preview</h2>
        <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnSecondary}>← Back to List</button>
      </div>

      {/* Exam Header */}
      <div style={{ background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #ddd', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>{exam.exam_name}</h3>
          <span style={{ padding: '4px 12px', background: colors.bg, color: '#fff', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            {colors.label}
          </span>
        </div>
        <p style={{ margin: '4px 0', color: '#666' }}>
          <strong>Level:</strong> {exam.level || 'All'} &nbsp;|&nbsp; 
          <strong>Created:</strong> {formatDate(exam.created_at)} &nbsp;|&nbsp; 
          <strong>Questions:</strong> {questions.length}
        </p>
        {exam.description && <p style={{ margin: '8px 0', fontStyle: 'italic', color: '#555' }}>{exam.description}</p>}
      </div>

      {/* Questions Flow */}
      <h3 style={{ marginBottom: 15 }}>Question Flow</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {questions.map((q, idx) => {
          // ✅ Use flow_id to find next questions
          const nextCorrect = findQuestionByFlowId(q.next_correct_flow_id);
          const nextWrong = findQuestionByFlowId(q.next_wrong_flow_id);

          return (
            <div key={q.flow_id} style={{ 
              padding: 15, 
              background: q.is_start ? '#e7f1ff' : '#fff',
              border: `2px solid ${q.is_start ? '#007BFF' : '#ddd'}`,
              borderRadius: 8,
              position: 'relative'
            }}>
              {q.is_start && (
                <span style={{ 
                  position: 'absolute', top: -10, right: 15, 
                  padding: '2px 8px', background: '#007BFF', color: '#fff',
                  borderRadius: 4, fontSize: 10, fontWeight: 600 
                }}>
                  START
                </span>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <strong style={{ fontSize: 14 }}>Q{idx + 1}:</strong> 
                  <span style={{ marginLeft: 8, fontSize: 14 }}>{q.question_text}</span>
                </div>
                <span style={{ fontSize: 11, color: '#666', background: '#f8f9fa', padding: '2px 6px', borderRadius: 4 }}>
                  Flow-ID: {q.flow_id}
                </span>
              </div>

              {/* Question context */}
              <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                {exam.exam_topic === 'english' 
                  ? `Band: ${q.band1 || '—'} / ${q.band2 || '—'}` 
                  : `Chapter: ${q.chapter || '—'}`}
                {q.question_tag_1 || q.question_tag_2 || q.question_tag_3 ? (
                  <span style={{ marginLeft: 12, color: '#6f42c1' }}>
                    Tags: {[q.question_tag_1, q.question_tag_2, q.question_tag_3].filter(t => t).join(', ')}
                  </span>
                ) : null}
              </div>

              {/* Answers */}
              <div style={{ fontSize: 13, background: '#f8f9fa', padding: 8, borderRadius: 6, marginBottom: 10 }}>
                <div><strong>A:</strong> {q.answer_a} {q.correct_answer === 'A' && '✅'}</div>
                <div><strong>B:</strong> {q.answer_b} {q.correct_answer === 'B' && '✅'}</div>
                <div><strong>C:</strong> {q.answer_c} {q.correct_answer === 'C' && '✅'}</div>
                <div><strong>D:</strong> {q.answer_d} {q.correct_answer === 'D' && '✅'}</div>
              </div>

              {/* ✅ Branching Logic - Now using correct lookup */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
                <div style={{ padding: 8, background: '#d4edda', borderRadius: 6, border: '1px solid #c3e6cb' }}>
                  <strong style={{ color: '#155724' }}>✓ If Correct →</strong><br/>
                  <span style={{ color: '#007BFF' }}>{getBranchTargetText(q.next_correct_flow_id)}</span>
                </div>
                <div style={{ padding: 8, background: '#f8d7da', borderRadius: 6, border: '1px solid #f5c6cb' }}>
                  <strong style={{ color: '#721c24' }}>✗ If Wrong →</strong><br/>
                  <span style={{ color: '#007BFF' }}>{getBranchTargetText(q.next_wrong_flow_id)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div style={{ marginTop: 25, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={() => navigate('/admin/flow-builder-exams')} style={btnSecondary}>← Back to List</button>
        <button onClick={() => navigate(`/admin/exam-builder?edit=${id}`)} style={{ ...btnSecondary, background: '#007BFF' }}>✏️ Edit Exam</button>
      </div>
    </div>
  );
}